import type Quill from "quill";
import type { ActiveLinkRef, LinkRangeRef } from "./types";
import { createQuillRangeAnchor } from "./utils";

export class EditorLinkResolver {
    constructor(
        private widgetId: string,
        private quill: Quill,
    ) {}

    resolveActiveLink(): ActiveLinkRef | null {
        const range = this.quill.getSelection();
        if (!range) {
            return null;
        }

        const resolved =
            range.length > 0
                ? this.resolveFromExpandedSelection(range.index, range.length)
                : this.resolveFromCaret(range.index);

        if (!resolved) {
            return null;
        }

        return {
            widgetId: this.widgetId,
            url: resolved.url,
            mode: "editing",
            range: resolved,
            anchor: createQuillRangeAnchor(
                `${this.widgetId}:${resolved.index}:${resolved.length}:${resolved.url}`,
                this.quill,
                resolved.index,
                resolved.length,
            ),
        };
    }

    resolveFromDomLink(linkEl: HTMLAnchorElement): ActiveLinkRef | null {
        const url = linkEl.getAttribute("href");
        if (!url) {
            return null;
        }

        return {
            widgetId: this.widgetId,
            url,
            mode: "editing",
            anchor: {
                key: `${this.widgetId}:dom:${url}`,
                getBoundingClientRect: () => linkEl.getBoundingClientRect(),
            },
        };
    }

    private resolveFromCaret(index: number): LinkRangeRef | null {
        const at = this.getLinkAt(index);
        if (at) {
            return at;
        }

        if (index > 0) {
            return this.getLinkAt(index - 1);
        }

        return null;
    }

    private resolveFromExpandedSelection(index: number, length: number): LinkRangeRef | null {
        const start = this.getLinkAt(index);
        if (!start) return null;

        const end = this.getLinkAt(index + Math.max(0, length - 1));
        if (!end) return null;

        if (start.url !== end.url) {
            return null;
        }

        return this.expandContiguousLink(index, start.url);
    }

    private getLinkAt(index: number): LinkRangeRef | null {
        const formats = this.quill.getFormat(index, 1);
        const url = typeof formats.link === "string" ? formats.link : null;
        if (!url) return null;

        return this.expandContiguousLink(index, url);
    }

    private expandContiguousLink(index: number, url: string): LinkRangeRef {
        let start = index;
        let end = index;

        while (start > 0) {
            const prev = this.quill.getFormat(start - 1, 1);
            if (prev.link !== url) break;
            start--;
        }

        const lastTextIndex = Math.max(0, this.quill.getLength() - 2);

        while (end < lastTextIndex) {
            const next = this.quill.getFormat(end + 1, 1);
            if (next.link !== url) break;
            end++;
        }

        return {
            index: start,
            length: end - start + 1,
            url,
        };
    }
}
